import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, MessageSquare, Search, Sparkles, Wallet } from 'lucide-react';
import { useSeo, useSeoCatalogue, useSeoContent } from '@/hooks/useSeo';
import { BrandMark } from '@/components/BrandMark';
import { Button, Spinner } from '@/components/ui';
import {
  BUY_GUIDE_SLUG,
  SELL_GUIDE_SLUG,
  SITE_NAME,
  categoryPath,
  plural,
} from '@/lib/seo';
import { formatNumber } from '@/lib/format';
import type { SeoFaqEntry } from '@/lib/seo';

interface Point {
  icon: typeof Search;
  title: string;
  body: string;
}

const BUY_POINTS: Point[] = [
  {
    icon: Sparkles,
    title: 'Pretraga koja razume rečenicu',
    body: 'Upišite „džip do 8.000 €” ili „Audi A4 dizel automatik”. Rečenica se pretvara u filtere, pa je pronalazak auta brz i lak.',
  },
  {
    icon: Wallet,
    title: 'Bez provizije i bez naplate',
    body: 'Pregled oglasa, čuvanje i poruke su besplatni. Dogovor je isključivo između vas i prodavca.',
  },
  {
    icon: MessageSquare,
    title: 'Direktan kontakt sa prodavcem',
    body: 'Pišite prodavcu preko sajta ili ga pozovite. Nema posrednika koji naplaćuje da vas spoji.',
  },
];

const SELL_POINTS: Point[] = [
  {
    icon: BadgeCheck,
    title: 'Oglas je besplatan, zauvek',
    body: 'Bez naplate oglasa, bez plaćenog isticanja i bez provizije od prodaje. Ono što dogovorite, to i dobijate.',
  },
  {
    icon: Search,
    title: 'Odmah u pretrazi',
    body: 'Čim objavite, oglas je vidljiv kupcima i pojavljuje se u rezultatima kao i svaki drugi.',
  },
  {
    icon: MessageSquare,
    title: 'Kupci pišu vama',
    body: 'Poruke stižu direktno, a broj telefona objavljujete samo ako želite.',
  },
];

/**
 * The questions, rendered as text on the page.
 *
 * The identical text is emitted by the server as FAQ structured data. Google requires that the
 * answers in that markup are visible to a visitor, and treats markup that is not as a violation
 * rather than as a missed opportunity — so this section is what makes the markup legitimate, not
 * an optional decoration on top of it.
 */
function Faq({ entries }: { entries: SeoFaqEntry[] }) {
  if (entries.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12">
      <h2 className="font-display text-2xl font-bold text-body">Česta pitanja</h2>

      <dl className="mt-6 divide-y divide-[var(--hairline)] border-y border-default">
        {entries.map((entry) => (
          <div key={entry.question} className="py-5">
            <dt className="text-base font-semibold text-body">{entry.question}</dt>
            <dd className="mt-2 text-sm leading-relaxed text-muted">{entry.answer}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Points({ points }: { points: Point[] }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-10">
      <div className="grid gap-4 sm:grid-cols-3">
        {points.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card"
          >
            <span className="flex size-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Icon size={20} />
            </span>
            <h2 className="mt-4 text-base font-semibold text-body">{title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/** The marque links, so a guide page is not a dead end for a crawler or for a reader. */
function PopularMakes() {
  const { data } = useSeoCatalogue();

  const makes = (data?.makes ?? []).slice(0, 12);

  if (makes.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-7xl px-4 pb-14">
      <h2 className="font-display text-lg font-bold text-body">Najtraženije marke</h2>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {makes.map((make) => (
          <Link
            key={make.slug}
            to={categoryPath(make.slug)}
            viewTransition
            className="press flex items-center gap-2.5 rounded-xl border border-default bg-raised px-3 py-2.5 transition hover:border-accent hover:shadow-card"
          >
            <BrandMark make={{ name: make.name }} size="sm" className="shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-body">
                Polovni {make.name}
              </span>
              <span className="block text-[11px] text-faint">
                {formatNumber(make.listingCount)}{' '}
                {plural(make.listingCount, 'oglas', 'oglasa', 'oglasa')}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function GuideLayout({
  slug,
  title,
  description,
  points,
  primary,
}: {
  slug: string;
  title: string;
  description: string;
  points: Point[];
  primary: { to: string; label: string };
}) {
  const { data, isPending } = useSeoContent();

  const guide = data?.guides.find((candidate) => candidate.slug === slug);

  useSeo({ title, description, canonicalPath: `/${slug}` });

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="aurora">
          <span />
        </div>

        <div className="relative mx-auto max-w-3xl px-4 pt-12 pb-8 text-center sm:pt-16">
          <h1 className="font-display text-4xl leading-[1.05] font-bold text-balance text-body sm:text-5xl">
            {guide?.heading ?? title.split('—')[0].trim()}
          </h1>

          {isPending ? (
            <div className="mt-6 flex justify-center">
              <Spinner className="size-6 text-accent" />
            </div>
          ) : (
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted">
              {guide?.intro ?? description}
            </p>
          )}

          <div className="mt-7 flex flex-wrap justify-center gap-2.5">
            <Link to={primary.to} viewTransition>
              <Button size="lg">
                {primary.label}
                <ArrowRight size={17} />
              </Button>
            </Link>
            <Link to={categoryPath()} viewTransition>
              <Button variant="secondary" size="lg">
                Polovni automobili
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Points points={points} />

      <Faq entries={guide?.faq ?? []} />

      <PopularMakes />
    </div>
  );
}

/** `/kupujem-auto` — the buyer's landing page. */
export function BuyGuidePage() {
  return (
    <GuideLayout
      slug={BUY_GUIDE_SLUG}
      title={`Kupujem auto — brz i lak pronalazak auta | ${SITE_NAME}`}
      description="Kupujem auto? Pretražite polovne automobile u Srbiji na jednom mestu. Brz i lak pronalazak auta, kontakt direktno sa prodavcem, bez reklama i bez provizije."
      points={BUY_POINTS}
      primary={{ to: '/', label: 'Pretraži automobile' }}
    />
  );
}

/** `/prodajem-auto` — the seller's landing page. */
export function SellGuidePage() {
  return (
    <GuideLayout
      slug={SELL_GUIDE_SLUG}
      title={`Prodajem auto — besplatan oglas za prodaju automobila | ${SITE_NAME}`}
      description="Prodajem auto? Postavite besplatan oglas za prodaju automobila. Bez naplate, bez provizije i bez reklama — kupci vas kontaktiraju direktno."
      points={SELL_POINTS}
      primary={{ to: '/postavi-oglas', label: 'Postavi besplatan oglas' }}
    />
  );
}
